/**
 * AssetSelection
 *
 * Step 1 of the Print Wizard.
 * Allows users to select assets and set quantities for label printing.
 * Supports search filtering and bulk selection.
 */
import {
  Add as AddIcon,
  Close,
  Remove as RemoveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Asset } from '../../types';
import type { PrintAssetSelection } from '../../types/printing';

interface AssetSelectionProps {
  selections: PrintAssetSelection[];
  onSelectionsChange: (selections: PrintAssetSelection[]) => void;
  onNext: () => void;
}

export function AssetSelection({ selections, onSelectionsChange, onNext }: AssetSelectionProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function fetchAssets() {
      try {
        const { data, error } = await supabase
          .from('assets')
          .select(
            'id, customer_id, tag_id, serial_number, type, status, created_at, updated_at, customer:customers(id, name)'
          )
          .eq('status', 'active')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setAssets((data as Asset[]) || []);
      } catch (err) {
        console.error('Error fetching assets:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchAssets();
  }, []);

  const filteredAssets = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return assets;
    return assets.filter(
      a =>
        a.serial_number?.toLowerCase().includes(term) ||
        a.tag_id.toLowerCase().includes(term) ||
        a.type?.toLowerCase().includes(term) ||
        a.customer?.name?.toLowerCase().includes(term)
    );
  }, [assets, search]);

  const selectedIds = useMemo(() => new Set(selections.map(s => s.asset.id)), [selections]);

  const toggleAsset = (asset: Asset) => {
    if (selectedIds.has(asset.id)) {
      onSelectionsChange(selections.filter(s => s.asset.id !== asset.id));
    } else {
      onSelectionsChange([...selections, { asset, quantity: 1 }]);
    }
  };

  const updateQuantity = (assetId: string, delta: number) => {
    onSelectionsChange(
      selections.map(s => {
        if (s.asset.id !== assetId) return s;
        const newQty = Math.max(1, Math.min(100, s.quantity + delta));
        return { ...s, quantity: newQty };
      })
    );
  };

  const setQuantity = (assetId: string, qty: number) => {
    const clamped = Math.max(1, Math.min(100, qty));
    onSelectionsChange(
      selections.map(s => (s.asset.id === assetId ? { ...s, quantity: clamped } : s))
    );
  };

  const selectAll = () => {
    const newSelections = filteredAssets
      .filter(a => !selectedIds.has(a.id))
      .map(asset => ({ asset, quantity: 1 }));
    onSelectionsChange([...selections, ...newSelections]);
  };

  const clearAll = () => onSelectionsChange([]);

  const totalLabels = selections.reduce((sum, s) => sum + s.quantity, 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Select Assets to Print
      </Typography>

      <TextField
        placeholder="Search by serial number, tag, type, or customer..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      <Grid container spacing={2}>
        {/* Available assets */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Available Assets ({filteredAssets.length})
                </Typography>
                <Button size="small" onClick={selectAll}>
                  Select All
                </Button>
              </Stack>
            </CardContent>
            <List
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                px: 1,
              }}
            >
              {loading ? (
                <ListItem>
                  <ListItemText primary="Loading assets..." />
                </ListItem>
              ) : filteredAssets.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No assets found"
                    secondary={
                      search ? 'Try a different search term' : 'No active assets available'
                    }
                  />
                </ListItem>
              ) : (
                filteredAssets.map(asset => (
                  <ListItem key={asset.id} dense disablePadding sx={{ px: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIds.has(asset.id)}
                          onChange={() => toggleAsset(asset)}
                          size="small"
                        />
                      }
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {asset.serial_number || asset.tag_id}
                          </Typography>
                          {asset.type && (
                            <Chip label={asset.type} size="small" variant="outlined" />
                          )}
                          {asset.customer?.name && (
                            <Typography variant="caption" color="text.secondary">
                              {asset.customer.name}
                            </Typography>
                          )}
                        </Stack>
                      }
                      sx={{ width: '100%' }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Card>
        </Grid>

        {/* Selected assets with quantities */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent sx={{ pb: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Selected ({selections.length} assets, {totalLabels} labels)
                </Typography>
                {selections.length > 0 && (
                  <Button size="small" color="error" onClick={clearAll}>
                    Clear
                  </Button>
                )}
              </Stack>
            </CardContent>
            <List sx={{ maxHeight: 400, overflow: 'auto', px: 1 }}>
              {selections.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No assets selected"
                    secondary="Select assets from the list on the left"
                  />
                </ListItem>
              ) : (
                selections.map(sel => (
                  <ListItem key={sel.asset.id} dense disablePadding>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: '100%', px: 1, py: 0.5 }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {sel.asset.serial_number || sel.asset.tag_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {sel.asset.type || 'No type'}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(sel.asset.id, -1)}
                          disabled={sel.quantity <= 1}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={sel.quantity}
                          onChange={e => setQuantity(sel.asset.id, parseInt(e.target.value) || 1)}
                          type="number"
                          size="small"
                          sx={{ width: 60 }}
                          slotProps={{
                            htmlInput: { min: 1, max: 100, style: { textAlign: 'center' } },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(sel.asset.id, 1)}
                          disabled={sel.quantity >= 100}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => toggleAsset(sel.asset)}
                          color="error"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </ListItem>
                ))
              )}
            </List>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={onNext}
          disabled={selections.length === 0}
          size="large"
        >
          Next: Preview & Print ({totalLabels} label{totalLabels !== 1 ? 's' : ''})
        </Button>
      </Stack>
    </Box>
  );
}
